import api from '@/services/api';
import type { QuoteSummary } from '../types/quote.types';
import { isDateKey, toDateKey, type DateKey } from '../utils/dateKey';
import {
    toWireService,
    fromWireService,
    toWireAddOn,
    fromWireAddOn,
    toPaymentSelectionPayload,
    fromPaymentSelectionPayload,
    toScheduleOverridesPayload,
    fromScheduleOverridesPayload,
    type QuoteRequest,
    type QuoteResponse,
    type QuoteListResponse,
} from './quoteWire';
import {
    toSmriqConfirmedVehiclePayload,
    fromSmriqConfirmedVehiclePayload,
    toSmriqPlanSettingsPayload,
    fromSmriqPlanSettingsPayload,
} from './smriqWire';

export type {
    QuoteRequest,
    QuoteResponse,
    QuoteListResponse,
    PaymentSelectionPayload,
    ScheduleOverridesPayload,
} from './quoteWire';

const quotePath = '/quotes';

// The money boundary fails closed: `total_cost` is only ever the server's figure, so a summary
// missing it or its `pricingVersion` is refused here rather than persisted as an unpriced zero.
const pricedPayload = (
    quoteSummary: Pick<QuoteSummary, 'total_cost' | 'pricingVersion'>,
): { total_cost: number; pricing_version: string } => {
    const { total_cost: totalCost, pricingVersion } = quoteSummary;
    if (typeof totalCost !== 'number' || !Number.isFinite(totalCost) || !pricingVersion) {
        throw new Error('Cannot save a quote without a server-derived price.');
    }
    return { total_cost: totalCost, pricing_version: pricingVersion };
};

const requireVersion = (version: unknown): number => {
    if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
        throw new Error('Malformed quote response: missing version.');
    }
    return version;
};

// Pricing drops an anchor that fails isDateKey, so save drops it too rather than
// sending an impossible calendar date the server rejects.
const validAnchorDate = (value: unknown): DateKey | null => (isDateKey(value) ? value : null);

const fromQuoteResponse = (data: QuoteResponse['data']): QuoteSummary => ({
    id: data.id,
    quote_ref: data.quote_ref,
    status: (data.status as 'live' | 'dead' | 'completed') || ('live' as const),
    version: requireVersion(data.version),
    updatedAt: data.updated_at,
    dealer: data.dealer,
    vehicle: data.vehicle,
    product: data.product,
    services: (data.services || []).map(fromWireService),
    addOns: (data.add_ons || []).map(fromWireAddOn),
    payment: data.payment,
    paymentSelection: fromPaymentSelectionPayload(data.payment_selection),
    scheduleOverrides: fromScheduleOverridesPayload(data.schedule_overrides),
    customer: data.customer,
    total_cost: data.total_cost,
    estimated_start_date: toDateKey(data.estimated_start_date),
    anchorDate: validAnchorDate(toDateKey(data.anchor_date)),
    resolvedPlanId: data.plan_id ?? null,
    servicePlanSessionId: data.service_plan_session_id ?? null,
    smriqConfirmedVehicle: fromSmriqConfirmedVehiclePayload(data.smriq_confirmed_vehicle),
    smriqPlanSettings: fromSmriqPlanSettingsPayload(data.smriq_plan_settings),
});

export const createQuote = async (quoteSummary: QuoteSummary): Promise<QuoteSummary> => {
    const priced = pricedPayload(quoteSummary);
    const quotePayload: QuoteRequest = {
        dealer: quoteSummary.dealer,
        vehicle: quoteSummary.vehicle,
        product: quoteSummary.product,
        services: quoteSummary.services.map(toWireService),
        add_ons: quoteSummary.addOns.map(toWireAddOn),
        payment: quoteSummary.payment,
        customer: quoteSummary.customer,
        // #872: total_cost is server-derived now; saveQuote() fills it in from the
        // priced basket rather than the snapshot computing it.
        total_cost: priced.total_cost,
        // #766: already a `DateKey` or `null` — never `.toISOString()`, which
        // would reintroduce the timezone bug class `dateKey.ts` exists to
        // prevent.
        estimated_start_date: quoteSummary.estimated_start_date,
        anchor_date: validAnchorDate(quoteSummary.anchorDate),
        plan_id: quoteSummary.resolvedPlanId ?? null,
        campaign_id: quoteSummary.product?.campaign?.[0]?.id ?? null,
        service_plan_session_id: quoteSummary.servicePlanSessionId ?? null,
        pricing_version: priced.pricing_version,
    };

    const paymentSelectionPayload = toPaymentSelectionPayload(quoteSummary.paymentSelection);
    if (paymentSelectionPayload) {
        quotePayload.payment_selection = paymentSelectionPayload;
    }
    const scheduleOverridesPayload = toScheduleOverridesPayload(quoteSummary.scheduleOverrides);
    if (scheduleOverridesPayload) {
        quotePayload.schedule_overrides = scheduleOverridesPayload;
    }
    // Only sent when a snapshot exists -- a non-SMRiQ quote never carries one.
    const smriqConfirmedVehiclePayload = toSmriqConfirmedVehiclePayload(
        quoteSummary.smriqConfirmedVehicle
    );
    if (smriqConfirmedVehiclePayload) {
        quotePayload.smriq_confirmed_vehicle = smriqConfirmedVehiclePayload;
    }
    const smriqPlanSettingsPayload = toSmriqPlanSettingsPayload(quoteSummary.smriqPlanSettings);
    if (smriqPlanSettingsPayload) {
        quotePayload.smriq_plan_settings = smriqPlanSettingsPayload;
    }
    if (quoteSummary.dealerProvisional === true) {
        quotePayload.dealer_provisional = true;
    }

    // Suppress global 403 toast: the save lane's error modal is the sole 403 surface.
    const response = await api.post<QuoteResponse>(`${quotePath}/`, quotePayload, {
        suppressGlobalForbiddenToast: true,
    });
    if (!response.data.success) {
        throw new Error('Failed to create quote');
    }

    return fromQuoteResponse(response.data.data);
};

export const updateQuote = async (
    quoteId: string,
    quoteSummary: Partial<QuoteSummary>,
    expectedVersion?: number,
): Promise<QuoteSummary> => {
    const quotePayload: Partial<QuoteRequest> = {};

    if (quoteSummary.dealer) quotePayload.dealer = quoteSummary.dealer;
    if (quoteSummary.vehicle) quotePayload.vehicle = quoteSummary.vehicle;
    if (quoteSummary.product) {
        quotePayload.product = quoteSummary.product;
        // campaign_id follows the product 1:1 (#639); only sent when a product
        // (hence its campaign) is present in this partial update, mirroring
        // the plan_id partial-update guard below.
        quotePayload.campaign_id = quoteSummary.product.campaign?.[0]?.id ?? null;
    }
    if (quoteSummary.services) quotePayload.services = quoteSummary.services.map(toWireService);
    if (quoteSummary.addOns) quotePayload.add_ons = quoteSummary.addOns.map(toWireAddOn);
    if (quoteSummary.payment) quotePayload.payment = quoteSummary.payment;
    const paymentSelectionPayload = toPaymentSelectionPayload(quoteSummary.paymentSelection);
    if (paymentSelectionPayload) {
        quotePayload.payment_selection = paymentSelectionPayload;
    }
    // Key presence, not truthiness: quote-service leaves an omitted
    // `schedule_overrides` alone, so an explicit clear must go as null.
    if ('scheduleOverrides' in quoteSummary) {
        quotePayload.schedule_overrides =
            toScheduleOverridesPayload(quoteSummary.scheduleOverrides) ?? null;
    }
    if (quoteSummary.customer) quotePayload.customer = quoteSummary.customer;
    // Sent as a pair or not at all, so a partial update can never move the stored total
    // away from the `pricing_version` that justifies it.
    if (quoteSummary.total_cost !== undefined || quoteSummary.pricingVersion !== undefined) {
        const priced = pricedPayload(quoteSummary);
        quotePayload.total_cost = priced.total_cost;
        quotePayload.pricing_version = priced.pricing_version;
        if (quoteSummary.anchorDate !== undefined) {
            quotePayload.anchor_date = validAnchorDate(quoteSummary.anchorDate);
        }
    }
    // #766/D5: `!== undefined`, NOT a truthy guard. A truthy guard would
    // silently OMIT the key when the value has become `null` (the date could
    // no longer be established), leaving a STALE date persisted — exactly
    // the failure AC2 describes. `undefined` omits the key (leave the stored
    // value alone); an explicit `null` is sent and CLEARS it.
    if (quoteSummary.estimated_start_date !== undefined) {
        quotePayload.estimated_start_date = quoteSummary.estimated_start_date;
    }
    // Partial-update guard: only send plan_id when the caller included
    // resolvedPlanId in the summary (undefined omits it; null clears it).
    if (quoteSummary.resolvedPlanId !== undefined) {
        quotePayload.plan_id = quoteSummary.resolvedPlanId;
    }
    // Same guard as plan_id: omitted leaves the stored session id alone, an
    // explicit null clears it.
    if (quoteSummary.servicePlanSessionId !== undefined) {
        quotePayload.service_plan_session_id = quoteSummary.servicePlanSessionId;
    }
    // Key presence, not truthiness: an omitted key leaves the stored snapshot
    // alone, an explicit null clears it.
    if ('smriqConfirmedVehicle' in quoteSummary) {
        quotePayload.smriq_confirmed_vehicle =
            toSmriqConfirmedVehiclePayload(quoteSummary.smriqConfirmedVehicle) ?? null;
    }
    // Same key-presence guard: an omitted key preserves the stored plan settings, an explicit
    // null clears them. Without this the amend path never persisted the duration/MOT/date.
    if ('smriqPlanSettings' in quoteSummary) {
        quotePayload.smriq_plan_settings =
            toSmriqPlanSettingsPayload(quoteSummary.smriqPlanSettings) ?? null;
    }
    if (quoteSummary.dealerProvisional === true) {
        quotePayload.dealer_provisional = true;
    }
    if (expectedVersion !== undefined) {
        quotePayload.expected_version = expectedVersion;
    }

    // Suppress global 403 toast: the save lane's error modal is the sole 403 surface.
    const response = await api.put<QuoteResponse>(`${quotePath}/${quoteId}`, quotePayload, {
        suppressGlobalForbiddenToast: true,
    });
    // Transform API response to match QuoteSummary structure
    if (!response.data.success) {
        throw new Error('Failed to update quote');
    }

    return fromQuoteResponse(response.data.data);
};

export const getQuote = async (quoteId: string): Promise<QuoteSummary> => {
    const response = await api.get<QuoteResponse>(`${quotePath}/${quoteId}`);
    // Transform API response to match QuoteSummary structure
    if (!response.data.success) {
        throw new Error('Failed to retrieve quote');
    }

    return fromQuoteResponse(response.data.data);
};

export const fetchQuotes = async (
    params: {
        search?: string;
        sort_by?: string;
        sort_direction?: 'asc' | 'desc';
        page?: number;
        per_page?: number;
    } = {}
): Promise<{ quotes: QuoteSummary[]; meta: QuoteListResponse['meta'] }> => {
    const queryParams = new URLSearchParams();

    if (params.search) queryParams.append('search', params.search);
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.sort_direction) queryParams.append('sort_direction', params.sort_direction);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.per_page) queryParams.append('per_page', params.per_page.toString());

    const queryString = queryParams.toString();
    const url = `${quotePath}/${queryString ? `?${queryString}` : ''}`;

    const response = await api.get<QuoteListResponse>(url);

    if (!response.data.success) {
        throw new Error('Failed to fetch quotes');
    }

    const quotes = response.data.data.map(fromQuoteResponse);

    return {
        quotes,
        meta: response.data.meta,
    };
};

export const completeQuote = async (
    quoteId: string,
    expectedVersion?: number,
): Promise<QuoteSummary> => {
    const body = expectedVersion === undefined ? undefined : { expected_version: expectedVersion };
    const response = await api.patch<QuoteResponse>(`${quotePath}/${quoteId}/complete`, body);

    if (!response.data.success) {
        throw new Error('Failed to complete quote');
    }

    return fromQuoteResponse(response.data.data);
};
